/* eslint-disable react/no-unescaped-entities */
/* eslint-disable react-hooks/exhaustive-deps */
// app/components/AboutUs.tsx
"use client";

import { useEffect, useRef, useState } from "react";
import {
  motion,
  useInView,
  useScroll,
  useTransform,
  AnimatePresence,
} from "framer-motion";
import { MapPin } from "lucide-react";

const GREEN = "rgb(34 150 83)";

/* ---------------- CLINIC DATA ---------------- */
const CLINICS = [

  {
    id: "dinnur-road",
    name: "Dinnur-road",
    address:
      "VG Complex, 36, Dinnur Main Rd, Near Ganesh Temple Road, Ganesha Block, Dinnur, RT Nagar, Bengaluru, Karnataka 560032",
    phone: "+91 84040 07002",
    hours: "Mon–Sat: 10 AM – 10 PM | Sun: 11 AM – 1 PM",
    slides: [
      { img: "/10.jpeg", label: "Consultation Room" },
      { img: "/11.jpeg", label: "Advanced Lab" },
      { img: "/13.jpeg", label: "Surgery Room" },
      { img: "/14.jpeg", label: "Patient Lounge" },
    ],
  },
];

/* ---------------- COUNTER ---------------- */
function useCounter(target: number, inView: boolean) {
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const inc = target / 90;

    const timer = setInterval(() => {
      start += inc;
      if (start >= target) {
        setVal(target);
        clearInterval(timer);
      } else {
        setVal(Math.floor(start));
      }
    }, 1000 / 60);

    return () => clearInterval(timer);
  }, [inView, target]);

  return val;
}

/* ---------------- COMPONENT ---------------- */
export default function AboutUs() {
  const rootRef = useRef<HTMLDivElement>(null);
  const inView = useInView(rootRef, { once: true, margin: "-10%" });

  const { scrollYProgress } = useScroll({
    target: rootRef,
    offset: ["start end", "end start"],
  });

  const yBg = useTransform(scrollYProgress, [0, 1], [0, -80]);

  const patients = useCounter(500, inView);
  const years = useCounter(4, inView);
  const specialists = useCounter(10, inView);

  const [activeClinic, setActiveClinic] = useState(0);
  const [activeSlide, setActiveSlide] = useState(0);

  useEffect(() => setActiveSlide(0), [activeClinic]);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSlide(
        (s) => (s + 1) % CLINICS[activeClinic].slides.length
      );
    }, 4500);
    return () => clearInterval(timer);
  }, [activeClinic]);

  const currentClinic = CLINICS[activeClinic];

  return (
    <section
      ref={rootRef}
      id="about"
      className="relative overflow-hidden bg-gradient-to-b from-white via-gray-50 to-white py-16 sm:py-20"
    >
      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
        {/* ---------- HEADER ---------- */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-12 sm:mb-16 text-center"
        >
          <span className="inline-block rounded-full border-2 border-emerald-200 bg-emerald-50 px-5 py-2 text-sm font-semibold text-emerald-700">
            ✨ About Pinak Dental Clinic
          </span>

          <h2 className="mt-5 text-3xl sm:text-4xl lg:text-6xl font-black text-gray-900">
            Trusted Dental Clinic in {" "} <br></br>
            <span className="bg-gradient-to-r from-emerald-600 to-emerald-400 bg-clip-text text-transparent">
               RT Nagar
            </span>
          </h2>
        </motion.div>

        {/* ---------- TABS ---------- */}
        <div className="mb-10 flex justify-center">
          <div className="flex flex-wrap justify-center gap-2 rounded-2xl border-2 border-emerald-200 bg-white p-2 shadow-lg">
            {CLINICS.map((clinic, index) => (
              <motion.button
                key={clinic.id}
                onClick={() => setActiveClinic(index)}
                className={`relative rounded-xl px-5 sm:px-8 py-2 sm:py-3 text-sm sm:text-lg font-bold transition-all ${activeClinic === index
                  ? "text-white"
                  : "text-gray-600 hover:text-gray-900"
                  }`}
              >
                {activeClinic === index && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute inset-0 rounded-xl"
                    style={{ backgroundColor: GREEN }}
                  />
                )}
                <span className="relative z-10 flex items-center gap-2">
                  <MapPin className="w-4 h-4 sm:w-5 sm:h-5" />
                  {clinic.name}
                </span>
              </motion.button>
            ))}
          </div>
        </div>

        {/* ---------- CONTENT ---------- */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeClinic}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 gap-10 lg:grid-cols-12"
          >
            {/* IMAGE SLIDER */}
            <div className="lg:col-span-5">
              <div className="relative h-[360px] sm:h-[460px] lg:h-[600px]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`${activeClinic}-${activeSlide}`}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.6 }}
                    className="absolute inset-0"
                  >
                    <div className="relative h-full overflow-hidden rounded-3xl border-4 border-white shadow-2xl">
                      <img
                        src={currentClinic.slides[activeSlide].img}
                        alt={currentClinic.slides[activeSlide].label}
                        className="h-full w-full object-cover"
                      />
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            {/* TEXT CONTENT */}
            <div className="lg:col-span-7">
              <div className="h-full rounded-3xl border-2 border-emerald-100 bg-white p-6 sm:p-8 shadow-2xl">
                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-black text-gray-900">
                  Pinak Dental Clinic –  RT Nagar 
                </h3>


                {/* ABOUT */}
                <div className="mt-8 space-y-4 border-l-4 border-emerald-500 pl-5">
                  <h4 className="text-lg sm:text-xl font-bold">
                    About Our Clinic
                  </h4>
                  <p className="text-gray-700 leading-relaxed">
                    Pinak Dental Clinic in RT Nagar delivers comprehensive dental care with advanced technology and personalized treatment plans. Conveniently located at &nbsp;
                    {currentClinic.address}, we're your trusted dental clinic, offering easy access, modern infrastructure, and patient comfort.
                  </p>
                  <p className="text-black leading-relaxed font-bold py-1">Flexible Hours: Mon–Sat: 10 AM – 10 PM | Sun: 11 AM – 1 PM
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    The clinic is equipped with modern infrastructure, strict
                    sterilization protocols, and thoughtfully designed treatment spaces
                    that prioritize hygiene, privacy, and patient comfort at every
                    step.
                  </p>
                </div>

                {/* WHY CHOOSE US */}
                <div className="mt-8 space-y-4 border-l-4 border-purple-500 pl-5">
                  <h4 className="text-lg sm:text-xl font-bold">
                    Why Choose Us?
                  </h4>
                  <p className="text-gray-700 leading-relaxed">
                    Experience precision dentistry and compassionate care tailored to you. Our 10+ specialists provide dental implants, root canals, pediatric dentistry, teeth whitening, crowns & bridges—backed by cutting-edge equipment, strict sterilization, and customized plans for lasting oral health and confident smiles.

                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    At Pinak Dental Clinic in RT Nagar, we believe that great dental care is not just about treatment — it’s
                    about education, prevention, and building long-term relationships
                    based on trust and results.
                  </p>
                </div>


              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* ---------- STATS ---------- */}
        <div className="mt-16 grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-4 ">
          {/* 1000+ Smiles */}
          <div className="rounded-3xl border border-emerald-200 bg-white/80 p-6 sm:p-8 text-center shadow-xl backdrop-blur">
            <p className="text-4xl sm:text-5xl lg:text-6xl font-black text-emerald-600">
              1000+
            </p>
            <p className="mt-2 text-base sm:text-lg font-semibold text-gray-800">
              Smiles Transformed
            </p>
          </div>

          {/* Google Rating */}
          <div className="rounded-3xl border border-yellow-200 bg-white/80 p-6 sm:p-8 text-center shadow-xl backdrop-blur">
            <p className="text-4xl sm:text-5xl lg:text-6xl font-black text-yellow-500">
              5★
            </p>
            <p className="mt-2 text-base sm:text-lg font-semibold text-gray-800">
              Google Rating
            </p>
          </div>

          {/* Patient Satisfaction */}
          <div className="rounded-3xl border border-blue-200 bg-white/80 p-6 sm:p-8 text-center shadow-xl backdrop-blur">
            <p className="text-4xl sm:text-5xl lg:text-6xl font-black text-blue-600">
              95%+
            </p>
            <p className="mt-2 text-base sm:text-lg font-semibold text-gray-800">
              Patient Satisfaction
            </p>
          </div>
          {/* Root Canals */}
          <div className="rounded-3xl border border-purple-200 bg-white/80 p-6 sm:p-8 text-center shadow-xl backdrop-blur sm:col-span-2 lg:col-span-1">
            <p className="text-4xl sm:text-5xl lg:text-6xl font-black text-purple-600">
              500+
            </p>
            <p className="mt-2 text-base sm:text-lg font-semibold text-gray-800">
              Root Canals Saved
            </p>
          </div>
        </div>

      </div>
    </section>
  );
}
